import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getDoc } from "firebase/firestore";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import SearchField from "../../../DesignSystem/Inputs/SearchField";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import IconButton from "../../../DesignSystem/Buttons/IconButton";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import { useAdviserListWithUnappointed } from "../../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";
import { useUserEmail } from "../../../SignIn/UserEmailContext";
import { getIODetailsRef } from "../../../Adviser/Tabs/IODetails/ioDetailsService";
import {
  getCurrentFCITotalsByAdviser,
  getFCIStatementsPage,
  getIOIntroducers,
} from "../../FCIStatements/fciStatementsService";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { useAdviserCharges } from "../context/AdviserChargesContext";
import { subscribeMonthlyManualPayments } from "../services/manualPaymentService";
import { subscribeFinplanStatement } from "../services/finplanStatementService";
import { subscribeQuilterStatement } from "../services/quilterStatementService";
import { subscribeSuspenseStatement } from "../services/suspenseStatementService";
import {
  saveManualPaymentRecipientMapping,
  subscribeIORecipientMappings,
} from "../services/ioRecipientMappingService";
import { saveClosingBalancesAsNextMonthOpeningBalances } from "../services/closingBalanceCarryForwardService";
import { calculateAccountBalances } from "../utils/accountBalances";
import { manualPaymentFeeEntries } from "../utils/manualPaymentCharges";
import { paymentsForMonth } from "../utils/paymentDates";
import { finplanCommissionRows } from "../utils/finplanAdviserLinks";
import { normaliseQuilterRows } from "../utils/quilterStatementParser";
import { normaliseSuspenseRows } from "../utils/suspenseStatementParser";
import { getIncomePeriodStartMonthKey, monthKeyFromDate } from "../utils/incomePeriod";
import {
  adviserIoDetailsDocumentId,
  uniqueIoRecipientIdsByEmail,
} from "../utils/ioRecipientMapping";
import {
  aggregateIoTotalsByRecipientId,
  ioIntroducerTotal,
  ioIntroducerTargets,
  mappedIoIntroducerGroups,
  mappedRecipientIdsByAdviser,
} from "../utils/ioRecipientTargets";
import { buildFeesAndChargesBreakdown } from "../components/CommissionStatementPDF/utils/feesAndChargesBreakdown";
import { formatMonthKey } from "../utils/chargeDates";
import { includedClosingBalanceRows } from "../utils/closingBalanceEligibility";
import { buildPaymentSummary } from "../utils/paymentSummary";
import { ReactComponent as ArrowRightIcon } from "../../../../assets/icons/arrow-right.svg";
import { ReactComponent as EditIcon } from "../../../../assets/icons/finance/Edit.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/paraplanningDashboard/Cross.svg";
import { ReactComponent as SortIcon } from "../../../../assets/icons/paraplanningDashboard/Sort.svg";
import OpeningBalanceBulkUpload from "../components/OpeningBalanceBulkUpload";

const OPENING_BALANCE_BULK_UPLOAD_ENABLED =
  String(process.env.REACT_APP_OPENING_BALANCE_BULK_UPLOAD_ENABLED || "")
    .trim()
    .toLowerCase() === "true";

const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    Number(value || 0),
  );
const keyOf = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const isNonZero = (value) => Math.round(Number(value || 0) * 100) !== 0;
const OpeningBalances = ({
  selectedMonth,
  monthOptions,
  onMonthChange,
  balanceView = "opening",
  onBalanceRowsChange,
}) => {
  const navigate = useNavigate();
  const { userEmail } = useUserEmail();
  const { advisers, loadingAdvisers } = useAdviserList();
  const {
    advisers: advisersWithUnappointed,
    loadingAdvisers: loadingAdvisersWithUnappointed,
  } = useAdviserListWithUnappointed();
  const {
    loadedChargeRecords,
    chargeRecords,
    advisersByEmail,
    incomePeriods,
    chargesLoading,
    selectedMonthClosed,
  } = useAdviserCharges();
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [sort, setSort] = useState({ key: "balance", direction: 1 });
  const [ioTotalsByEmail, setIOTotalsByEmail] = useState(new Map());
  const [ioTotalsByRecipientId, setIOTotalsByRecipientId] = useState(new Map());
  const [ioTotalsLoading, setIOTotalsLoading] = useState(true);
  const [ioIntroducers, setIoIntroducers] = useState([]);
  const [ioIntroducersLoading, setIoIntroducersLoading] = useState(true);
  const [ioRecipientMappings, setIoRecipientMappings] = useState({});
  const [quilterStatement, setQuilterStatement] = useState(null);
  const [quilterTotalsLoading, setQuilterTotalsLoading] = useState(true);
  const [finplanStatement, setFinplanStatement] = useState(null);
  const [finplanTotalsLoading, setFinplanTotalsLoading] = useState(true);
  const [suspenseStatement, setSuspenseStatement] = useState(null);
  const [suspenseLoading, setSuspenseLoading] = useState(true);
  const [suspenseLoadedMonth, setSuspenseLoadedMonth] = useState("");
  const [savingCarryForward, setSavingCarryForward] = useState(false);
  const [carryForwardMessage, setCarryForwardMessage] = useState("");
  const [carryForwardError, setCarryForwardError] = useState("");
  const [mappingRow, setMappingRow] = useState(null);
  const [mappingPosition, setMappingPosition] = useState(null);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [mappingError, setMappingError] = useState("");

  const availableAdvisers = useMemo(() => {
    const byEmail = new Map();
    [...advisers, ...advisersWithUnappointed].forEach((adviser) => {
      const email = keyOf(adviser.email || adviser.id);
      if (!email) return;
      byEmail.set(email, { ...byEmail.get(email), ...adviser, email });
    });
    return [...byEmail.values()];
  }, [advisers, advisersWithUnappointed]);

  const recipientMappingOptions = useMemo(
    () => [
      ...availableAdvisers
        .map((adviser) => {
          const email = keyOf(adviser.email || adviser.id);
          return {
            id: `adviser:${email}`,
            name: `[Adviser${adviser.isUnappointed ? " · Unappointed" : ""}] ${adviser.name || email} — ${email}`,
            target: {
              type: "adviser",
              targetId: email,
              email,
              name: adviser.name || email,
              adviser,
            },
          };
        })
        .filter((option) => option.target.targetId),
      ...ioIntroducerTargets(ioIntroducers).map((introducer) => ({
        id: introducer.id,
        name: introducer.optionName,
        target: introducer,
      })),
    ].sort((left, right) => left.name.localeCompare(right.name, "en-GB", {
      numeric: true,
    })),
    [availableAdvisers, ioIntroducers],
  );
  useEffect(
    () => subscribeMonthlyManualPayments(setPayments, () => setPayments([])),
    [],
  );
  useEffect(
    () => subscribeIORecipientMappings(setIoRecipientMappings, () => setIoRecipientMappings({})),
    [],
  );
  useEffect(() => {
    let active = true;
    setIoIntroducersLoading(true);
    getIOIntroducers(userEmail)
      .then((rows) => {
        if (active) setIoIntroducers(rows);
      })
      .catch(() => {
        if (active) setIoIntroducers([]);
      })
      .finally(() => {
        if (active) setIoIntroducersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [userEmail]);
  useEffect(() => {
    let active = true;
    setIOTotalsLoading(true);
    if (!incomePeriods) {
      return () => {
        active = false;
      };
    }
    const load = async () => {
      try {
        const [totals, adviserDetails] = await Promise.all([
          (async () => {
            const currentIncomeMonth = monthKeyFromDate(
              incomePeriods[0]?.endDatetime ||
                incomePeriods[0]?.endDate ||
                incomePeriods[0]?.incomePeriodEndDatetime,
            );
            if (selectedMonth === currentIncomeMonth) {
              return getCurrentFCITotalsByAdviser(userEmail);
            }
            const period = incomePeriods.find(
              (item) => getIncomePeriodStartMonthKey(item) === selectedMonth,
            );
            if (!period?.incomePeriodId) return [];
            const statement = await getFCIStatementsPage(1, 100000, userEmail, {
              _period: period.incomePeriodId,
              _sort: "PaymentCreated",
              _direction: "desc",
              _export: "true",
            });
            const totalsById = new Map();
            (statement.rows || []).forEach((row) => {
              const adviserId = String(
                row.RecipientCrmId ??
                  row.RecipientUserId ??
                  row.RecipientId ??
                  row.AdviserId ??
                  "",
              ).trim();
              if (!adviserId) return;
              const amount = Number(
                String(row.PaymentAmount ?? row.paymentAmount ?? 0).replace(/[,£]/g, ""),
              );
              if (Number.isFinite(amount)) {
                totalsById.set(adviserId, (totalsById.get(adviserId) || 0) + amount);
              }
            });
            return [...totalsById.entries()].map(([adviserId, paymentAmount]) => ({
              adviserId,
              paymentAmount,
            }));
          })(),
          Promise.allSettled(
            advisers.map(async (adviser) => {
              const email = adviser.email || adviser.id;
              if (!email) return null;
              const snapshot = await getDoc(
                getIODetailsRef(adviserIoDetailsDocumentId(adviser)),
              );
              return {
                email: keyOf(email),
                isAppointed: adviser.isAppointed,
                adviser,
                ioDetails: snapshot.exists() ? snapshot.data() : {},
              };
            }),
          ).then((results) => results
            .filter((result) => result.status === "fulfilled" && result.value)
            .map((result) => result.value)),
        ]);
        const totalsById = aggregateIoTotalsByRecipientId(totals);
        const next = new Map();
        const uniqueIds = uniqueIoRecipientIdsByEmail(
          adviserDetails.filter(Boolean),
        );
        const mappedIds = mappedRecipientIdsByAdviser(ioRecipientMappings);
        adviserDetails.filter(Boolean).forEach(({ email }) => {
          const recipientIds = new Set([
            ...(uniqueIds[email] || []),
            ...(mappedIds.get(email) || []),
          ].map(String));
          next.set(
            email,
            [...recipientIds].reduce(
              (sum, id) => sum + Number(totalsById.get(keyOf(id)) || 0),
              0,
            ),
          );
        });
        if (active) {
          setIOTotalsByEmail(next);
          setIOTotalsByRecipientId(totalsById);
        }
      } catch (error) {
        if (active) {
          setIOTotalsByEmail(new Map());
          setIOTotalsByRecipientId(new Map());
        }
      } finally {
        if (active) setIOTotalsLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [advisers, incomePeriods, ioRecipientMappings, selectedMonth, userEmail]);
  useEffect(() => {
    setQuilterTotalsLoading(true);
    return subscribeQuilterStatement(
      selectedMonth,
      (nextStatement) => {
        setQuilterStatement(nextStatement);
        setQuilterTotalsLoading(false);
      },
      () => {
        setQuilterStatement(null);
        setQuilterTotalsLoading(false);
      },
    );
  }, [selectedMonth]);
  useEffect(() => {
    setFinplanTotalsLoading(true);
    return subscribeFinplanStatement(
      selectedMonth,
      (nextStatement) => {
        setFinplanStatement(nextStatement);
        setFinplanTotalsLoading(false);
      },
      () => {
        setFinplanStatement(null);
        setFinplanTotalsLoading(false);
      },
    );
  }, [selectedMonth]);
  useEffect(() => {
    setSuspenseLoading(true);
    setSuspenseLoadedMonth("");
    return subscribeSuspenseStatement(
      selectedMonth,
      (nextStatement) => {
        setSuspenseStatement(nextStatement);
        setSuspenseLoadedMonth(selectedMonth);
        setSuspenseLoading(false);
      },
      () => {
        setSuspenseStatement(null);
        setSuspenseLoadedMonth(selectedMonth);
        setSuspenseLoading(false);
      },
    );
  }, [selectedMonth]);
  const quilterTotalsByEmail = useMemo(() => {
    const totals = new Map();
    if (!quilterStatement?.rows?.length) return totals;
    const advisersByName = new Map(advisers.map((adviser) => [keyOf(adviser.name), adviser]));
    const linksBySource = new Map((quilterStatement.adviserLinks || []).map((link) => [keyOf(link.sourceAdviserName), link]));
    normaliseQuilterRows(quilterStatement.rows).forEach((row) => {
      const override = linksBySource.get(keyOf(row.SourceAdviserName));
      const linked = override?.email ? override : advisersByName.get(keyOf(row.MappedAdviserName));
      const email = keyOf(linked?.email);
      const amount = Number(String(row.PaymentAmount ?? "").replace(/[,£]/g, ""));
      if (email && Number.isFinite(amount)) totals.set(email, (totals.get(email) || 0) + amount);
    });
    return totals;
  }, [advisers, quilterStatement]);
  const finplanTotalsByEmail = useMemo(() => {
    const totals = new Map();
    finplanCommissionRows({
      statement: finplanStatement,
      advisers,
      statementMonth: selectedMonth,
    }).forEach((row) => {
      const email = keyOf(row["Recipient Email"]);
      const amount = Number(String(row.PaymentAmount ?? 0).replace(/[,£]/g, ""));
      if (!email || !Number.isFinite(amount)) return;
      totals.set(email, (totals.get(email) || 0) + amount);
    });
    return totals;
  }, [advisers, finplanStatement, selectedMonth]);
  const suspenseByEmail = useMemo(() => {
    const totals = new Map();
    const advisersByName = new Map(
      advisers.map((adviser) => [keyOf(adviser.name), adviser]),
    );
    const linksBySource = new Map(
      (suspenseStatement?.adviserLinks || []).map((link) => [
        keyOf(link.sourceAdviserName),
        link,
      ]),
    );
    normaliseSuspenseRows(suspenseStatement?.rows || []).forEach((row) => {
      const override = linksBySource.get(keyOf(row.SourceAdviserName));
      const linked = override?.email
        ? override
        : advisersByName.get(keyOf(row.MappedAdviserName));
      const email = keyOf(linked?.email);
      if (!email) return;
      totals.set(email, [...(totals.get(email) || []), row]);
    });
    return totals;
  }, [advisers, suspenseStatement]);
  const baseRows = useMemo(() => {
    const { balances } = calculateAccountBalances({
      records: loadedChargeRecords,
      payments,
      month: selectedMonth,
    });
    const selectedPayments = paymentsForMonth(payments, selectedMonth);
    const bulkUploadedBalances = new Map();
    const nonAdviserBulkBalances = new Map();
    const nonAdviserOpeningBalances = new Map();
    const openingPaymentGroupsByEmail = new Map();
    const openingPayments = payments.filter(
      (payment) =>
        payment.paymentMonth === selectedMonth &&
        payment.paymentType === "opening_balance",
    );
    const nonAdviserIdentity = (payment) => {
      const entityType = String(payment.mappedEntityType || "unknown").toLowerCase();
      const entityId = String(
        payment.mappedEntityId ||
          payment.ioIntroducerId ||
          payment.sourceName ||
          payment.id ||
          "",
      ).trim();
      if (!entityId) return null;
      return {
        entityType,
        rowKey: `${entityType}:${entityId.toLowerCase()}`,
        name: payment.mappedEntityName || payment.sourceName || "Unknown",
      };
    };
    const manuallyUploadedNonAdvisers = new Set(
      openingPayments
        .filter(
          (payment) =>
            !keyOf(payment.adviserEmail) &&
            payment.source !== "closing_balance_carry_forward",
        )
        .map((payment) => nonAdviserIdentity(payment)?.rowKey)
        .filter(Boolean),
    );
    openingPayments
      .forEach((payment) => {
        const email = keyOf(payment.adviserEmail);
        if (!email) {
          const identity = nonAdviserIdentity(payment);
          if (!identity) return;
          if (
            payment.source === "closing_balance_carry_forward" &&
            manuallyUploadedNonAdvisers.has(identity.rowKey)
          ) return;
          const existingOpening = nonAdviserOpeningBalances.get(identity.rowKey) || {
            ...identity,
            amount: 0,
            paymentGroups: new Map(),
          };
          existingOpening.amount += Number(payment.openingBalanceAmount || 0);
          const sourceName = String(
            payment.sourceName || payment.mappedEntityName || identity.name,
          ).trim();
          const sourceKey = keyOf(sourceName) || identity.rowKey;
          const paymentGroup = existingOpening.paymentGroups.get(sourceKey) || {
            sourceName: sourceName || identity.name,
            paymentIds: [],
          };
          paymentGroup.paymentIds.push(payment.id);
          existingOpening.paymentGroups.set(sourceKey, paymentGroup);
          nonAdviserOpeningBalances.set(identity.rowKey, existingOpening);
          if (payment.source === "opening_balance_bulk_upload") {
            const existingBulk = nonAdviserBulkBalances.get(identity.rowKey) || {
              ...identity,
              amount: 0,
            };
            existingBulk.amount += Number(payment.openingBalanceAmount || 0);
            nonAdviserBulkBalances.set(identity.rowKey, existingBulk);
          }
          return;
        }
        const sourceName = String(
          payment.sourceName || payment.adviserName || email,
        ).trim();
        const sourceKey = keyOf(sourceName) || email;
        const paymentGroups = openingPaymentGroupsByEmail.get(email) || new Map();
        const paymentGroup = paymentGroups.get(sourceKey) || {
          sourceName: sourceName || email,
          paymentIds: [],
        };
        paymentGroup.paymentIds.push(payment.id);
        paymentGroups.set(sourceKey, paymentGroup);
        openingPaymentGroupsByEmail.set(email, paymentGroups);
        if (payment.source !== "opening_balance_bulk_upload") return;
        bulkUploadedBalances.set(
          email,
          (bulkUploadedBalances.get(email) || 0) +
            Number(payment.openingBalanceAmount || 0),
        );
      });
    const listedByEmail = new Map(
      advisers.map((adviser) => [keyOf(adviser.email || adviser.id), adviser]),
    );
    const emails = new Set([
      ...balances.keys(),
      ...chargeRecords.map((record) => keyOf(record.adviserEmail)),
      ...selectedPayments
        .map((payment) => keyOf(payment.adviserEmail)),
      ...bulkUploadedBalances.keys(),
      ...ioTotalsByEmail.keys(),
      ...quilterTotalsByEmail.keys(),
      ...finplanTotalsByEmail.keys(),
      ...suspenseByEmail.keys(),
    ].filter(Boolean));
    const adviserRows = [...emails].map((email) => {
      const listed = listedByEmail.get(email);
      const details = advisersByEmail[email]?.adviser;
      const openingBalance = Number(balances.get(email) || 0);
      const commission =
        Number(ioTotalsByEmail.get(email) || 0) +
        Number(quilterTotalsByEmail.get(email) || 0) +
        Number(finplanTotalsByEmail.get(email) || 0);
      const manualPaymentCharges = manualPaymentFeeEntries(
        selectedPayments.filter(
          (payment) => keyOf(payment.adviserEmail) === email,
        ),
      );
      const feesAndCharges = buildFeesAndChargesBreakdown(
        [
          ...chargeRecords.filter((charge) => keyOf(charge.adviserEmail) === email),
          ...manualPaymentCharges,
        ],
        suspenseByEmail.get(email) || [],
      );
      const paymentSummary = buildPaymentSummary({
        openingBalance,
        totalCommission: commission,
        feesAndCharges,
      });
      return {
        email,
        adviserId: listed?.adviserId || details?.adviserId || "",
        adviser: listed?.name || details?.adviser || email,
        practice:
          listed?.brandName || details?.brandName || details?.practice || "-",
        paymentGroups: [
          ...(openingPaymentGroupsByEmail.get(email)?.values() || []),
        ],
        openingBalance,
        hasOpeningBalance: balances.has(email),
        bulkUploadedOpeningBalance: bulkUploadedBalances.get(email) || 0,
        hasBulkUploadedOpeningBalance: bulkUploadedBalances.has(email),
        totalCommission: commission,
        totalCharged: paymentSummary.totalCharged,
        totalPayments: paymentSummary.payments,
        totalAdvances: paymentSummary.advances,
        balance: paymentSummary.balanceToPay,
        closingBalance: paymentSummary.balanceToPay,
      };
    });
    const nonAdviserKeys = new Set([
      ...nonAdviserOpeningBalances.keys(),
      ...nonAdviserBulkBalances.keys(),
    ]);
    const introducerGroupsByRowKey = new Map(
      mappedIoIntroducerGroups([], ioRecipientMappings).map((group) => {
        const rowKey = `introducer:${keyOf(group.targetId)}`;
        nonAdviserKeys.add(rowKey);
        return [rowKey, group];
      }),
    );
    const nonAdviserRows = [...nonAdviserKeys].map(
      (rowKey) => {
        const entity =
          nonAdviserOpeningBalances.get(rowKey) ||
          nonAdviserBulkBalances.get(rowKey) || {
            entityType: "introducer",
            rowKey,
            name: introducerGroupsByRowKey.get(rowKey)?.name || "IO Introducer",
            amount: 0,
            paymentGroups: new Map(),
          };
        const bulkEntity = nonAdviserBulkBalances.get(rowKey);
        const mappedEntityId = rowKey.slice(rowKey.indexOf(":") + 1);
        const introducerGroup = introducerGroupsByRowKey.get(rowKey);
        const totalCommission = entity.entityType === "introducer"
          ? ioIntroducerTotal({
              totalsByRecipientId: ioTotalsByRecipientId,
              targetId: mappedEntityId,
              recipientIds: introducerGroup?.recipientIds,
            })
          : 0;
        const feesAndCharges = buildFeesAndChargesBreakdown(
          manualPaymentFeeEntries(
            selectedPayments.filter(
              (payment) =>
                !keyOf(payment.adviserEmail) &&
                nonAdviserIdentity(payment)?.rowKey === rowKey,
            ),
          ),
        );
        const paymentSummary = buildPaymentSummary({
          openingBalance: entity.amount,
          totalCommission,
          feesAndCharges,
        });
        return {
        email: "",
        rowKey,
        adviser: entity.name,
        practice:
          entity.entityType === "introducer" ? "IO Introducer" : "Unknown",
        isUnknown: entity.entityType === "unknown",
        mappedEntityType: entity.entityType,
        mappedEntityId,
        paymentGroups: [...(entity.paymentGroups?.values() || [])],
        openingBalance: entity.amount,
        hasOpeningBalance: nonAdviserOpeningBalances.has(rowKey),
        bulkUploadedOpeningBalance: bulkEntity?.amount || 0,
        hasBulkUploadedOpeningBalance: Boolean(bulkEntity),
        totalCommission,
        totalCharged: paymentSummary.totalCharged,
        totalPayments: paymentSummary.payments,
        totalAdvances: paymentSummary.advances,
        balance: paymentSummary.balanceToPay,
        closingBalance: paymentSummary.balanceToPay,
      };
      },
    );
    return [...adviserRows, ...nonAdviserRows];
  }, [
    advisers,
    advisersByEmail,
    chargeRecords,
    finplanTotalsByEmail,
    loadedChargeRecords,
    ioTotalsByEmail,
    ioTotalsByRecipientId,
    ioRecipientMappings,
    payments,
    quilterTotalsByEmail,
    suspenseByEmail,
    selectedMonth,
  ]);
  const balanceAmount = (row) =>
    balanceView === "closing"
      ? row.closingBalance
      : row.openingBalance;
  const includedBalanceRows = useMemo(
    () =>
      (balanceView === "closing"
        ? includedClosingBalanceRows(baseRows)
        : baseRows.filter(
            (row) => row.hasOpeningBalance && isNonZero(row.openingBalance),
          )),
    [balanceView, baseRows],
  );
  const rows = useMemo(
    () =>
      includedBalanceRows
        .filter((row) =>
          `${row.adviser} ${row.email} ${row.practice}`
            .toLowerCase()
            .includes(deferredSearch.toLowerCase()),
        )
        .sort((left, right) => {
          const amountKey =
            balanceView === "closing"
              ? "closingBalance"
              : "openingBalance";
          const leftValue = sort.key === "balance" ? left[amountKey] : left[sort.key];
          const rightValue = sort.key === "balance" ? right[amountKey] : right[sort.key];
          const comparison =
            typeof leftValue === "number"
              ? leftValue - rightValue
              : String(leftValue).localeCompare(String(rightValue), "en-GB");
          return comparison * sort.direction;
        }),
    [balanceView, deferredSearch, includedBalanceRows, sort],
  );
  const closingDataLoading =
    chargesLoading ||
    !incomePeriods ||
    finplanTotalsLoading ||
    ioTotalsLoading ||
    quilterTotalsLoading ||
    suspenseLoading ||
    suspenseLoadedMonth !== selectedMonth;
  const incomePeriodAvailable = Boolean(
    incomePeriods?.some(
      (period) => getIncomePeriodStartMonthKey(period) === selectedMonth,
    ) ||
      monthKeyFromDate(
        incomePeriods?.[0]?.endDatetime ||
          incomePeriods?.[0]?.endDate ||
          incomePeriods?.[0]?.incomePeriodEndDatetime,
      ) === selectedMonth,
  );
  const tableLoading =
    ioTotalsLoading ||
    finplanTotalsLoading ||
    quilterTotalsLoading ||
    (balanceView === "closing" && closingDataLoading);
  useEffect(() => {
    if (balanceView !== "closing" || !onBalanceRowsChange) return;
    onBalanceRowsChange({
      month: selectedMonth,
      rows: includedBalanceRows,
      ready: !closingDataLoading,
    });
  }, [
    balanceView,
    closingDataLoading,
    includedBalanceRows,
    onBalanceRowsChange,
    selectedMonth,
  ]);
  const totalBalance = rows.reduce(
    (total, row) => total + balanceAmount(row),
    0,
  );
  const saveCarryForward = async () => {
    setSavingCarryForward(true);
    setCarryForwardMessage("");
    setCarryForwardError("");
    try {
      const result = await saveClosingBalancesAsNextMonthOpeningBalances({
        month: selectedMonth,
        rows: baseRows,
      });
      setCarryForwardMessage(
        `Saved ${result.count} closing balances as ${formatMonthKey(result.openingMonth)} opening balances.`,
      );
    } catch (error) {
      setCarryForwardError(error.message || "Unable to save closing balances.");
    } finally {
      setSavingCarryForward(false);
    }
  };
  const changeSort = (key) =>
    setSort((current) => ({
      key,
      direction: current.key === key ? -current.direction : 1,
    }));
  const adviserUrl = (row) =>
    `${APP_ROUTES.CHARGES_BREAKDOWN}/${encodeURIComponent(row.email)}?month=${selectedMonth}&view=${balanceView === "closing" ? "closing-balances" : "opening-balances"}`;
  const openMapping = (event, row) => {
    event.stopPropagation();
    const anchor = event.currentTarget.getBoundingClientRect();
    const popupWidth = 420;
    setMappingPosition({
      left: Math.max(
        12,
        Math.min(anchor.left, window.innerWidth - popupWidth - 12),
      ),
      top: Math.max(12, Math.min(anchor.bottom + 8, window.innerHeight - 360)),
    });
    setMappingError("");
    setMappingRow(row);
  };
  const closeMapping = () => {
    if (mappingSaving) return;
    setMappingRow(null);
    setMappingPosition(null);
    setMappingError("");
  };
  const saveMapping = async (optionId) => {
    const option = recipientMappingOptions.find((item) => item.id === optionId);
    if (!mappingRow || !option?.target) return;
    const paymentGroups = (mappingRow.paymentGroups || [])
      .map((group) => ({
        ...group,
        paymentIds: (group.paymentIds || []).filter(Boolean),
      }))
      .filter((group) => group.paymentIds.length);
    if (!paymentGroups.length) {
      setMappingError("No saved opening balance entries were found for this row.");
      return;
    }
    setMappingSaving(true);
    setMappingError("");
    try {
      await Promise.all(
        paymentGroups.map((group) =>
          saveManualPaymentRecipientMapping({
            paymentIds: group.paymentIds,
            sourceName: group.sourceName || mappingRow.adviser,
            target: option.target,
          }),
        ),
      );
      setMappingRow(null);
      setMappingPosition(null);
    } catch (error) {
      setMappingError(error.message || "The recipient mapping could not be updated.");
    } finally {
      setMappingSaving(false);
    }
  };
  return (
    <section className="column gap-l">
      <header className="row space-between gap-l">
        <h3 className="FP_H3">
          {balanceView === "closing" ? "Closing Balances" : "Opening Balances"}
        </h3>
      </header>
      <div className="opening-balances-toolbar">
        <RadioGroup
          name="opening-balances-month"
          options={monthOptions}
          value={selectedMonth}
          onChange={(event) => onMonthChange(event.target.value)}
          inline
        />
        <div className="opening-balances-toolbar__actions">
          <SearchField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search"
          />
          {balanceView === "closing" ? (
            <PrimaryButton
              type="button"
              label={savingCarryForward ? "Saving…" : "Save"}
              onClick={saveCarryForward}
              disabled={savingCarryForward || closingDataLoading || loadingAdvisers || !incomePeriodAvailable || !baseRows.length}
            />
          ) : null}
        </div>
      </div>
      {carryForwardMessage ? (
        <p className="FP_Content text-success" role="status">{carryForwardMessage}</p>
      ) : null}
      {carryForwardError ? (
        <p className="FP_Content text-error" role="alert">{carryForwardError}</p>
      ) : null}
      {balanceView === "closing" && !closingDataLoading && !incomePeriodAvailable ? (
        <p className="FP_Content text-error" role="alert">
          IO income for this month is not available yet. Closing balances cannot be carried forward.
        </p>
      ) : null}
      {balanceView === "opening" && OPENING_BALANCE_BULK_UPLOAD_ENABLED && selectedMonth === "2026-09" && !selectedMonthClosed ? (
        <OpeningBalanceBulkUpload
          advisers={advisers}
          loadingAdvisers={loadingAdvisers}
          monthKey={selectedMonth}
        />
      ) : null}
      {/* API fetch errors are intentionally not rendered in the UI. */}
      <div className="website-dashboard-card opening-balances-card">
        <div className="website-table-wrap table-scroll-container">
          <table className="website-table">
            <thead>
              <tr>
                <th className="FP_Help_Text_Semibold uppercase">Index</th>
                {[
                  ["adviser", "Adviser"],
                  ["practice", "Practice"],
                  [
                    "balance",
                    balanceView === "closing" ? "Closing Balance" : "Opening Balance",
                  ],
                ].map(([key, label]) => (
                  <th
                    key={key}
                    className={`FP_Help_Text_Semibold uppercase ${key === "balance" ? "website-charge-group-divider text-align-right" : ""}`}
                  >
                    <button
                      type="button"
                      className={`table-heading-content table-sort-button ${key === "balance" ? "width-full justify-flex-end" : ""}`}
                      onClick={() => changeSort(key)}
                    >
                      {label} <SortIcon aria-hidden="true" />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan="4" className="FP_Content">
                    Loading adviser income...
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((row, index) => (
                  <tr
                    key={row.email || row.rowKey}
                    className={!row.email ? "monthly-payment-unlinked-email-row" : ""}
                  >
                    <td className="FP_Help_Text black">{index + 1}</td>
                    <td className="FP_Help_Text_Semibold black">
                      <div className="row align-center gap-s">
                        {!selectedMonthClosed && row.paymentGroups?.some(
                          (group) => group.paymentIds?.length,
                        ) ? (
                          <IconButton
                            icon={<EditIcon aria-hidden="true" />}
                            label={`Change recipient mapping for ${row.adviser}`}
                            onClick={(event) => openMapping(event, row)}
                          />
                        ) : null}
                        <div className="fit-column gap-xs align-left">
                          {row.email ? (
                            <button
                              type="button"
                              className="table-adviser-link"
                              onClick={() => navigate(adviserUrl(row))}
                            >
                              {row.adviser}
                            </button>
                          ) : (
                            <span>{row.adviser}</span>
                          )}
                          <span className="FP_Help_Text black text-left">
                            {row.email || (row.mappedEntityType === "introducer"
                              ? "Linked to IO introducer"
                              : "Recipient not linked")}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="FP_Help_Text black">{row.practice}</td>
                    <td
                      className={`FP_Help_Text website-charge-group-divider ${balanceAmount(row) < 0 ? "text-error" : "black"}`}
                    >
                      <div className="row width-full justify-flex-end align-center gap-m">
                        <span>{money(balanceAmount(row))}</span>
                        {row.email ? (
                          <button
                            type="button"
                            className="table-row-action"
                            aria-label={`View ${row.adviser} ${balanceView} balance`}
                            onClick={() => navigate(adviserUrl(row))}
                          >
                            <ArrowRightIcon aria-hidden="true" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="FP_Content">
                    No {balanceView} balances for this month.
                  </td>
                </tr>
              )}
            </tbody>
            {!tableLoading ? (
              <tfoot>
                <tr className="opening-balances-total-row">
                  <td className="FP_Help_Text_Semibold" colSpan="3">Total</td>
                  <td
                    className={`FP_Help_Text_Semibold website-charge-group-divider text-align-right ${totalBalance < 0 ? "text-error" : "black"}`}
                  >
                    {money(totalBalance)}
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </div>
      {mappingRow && mappingPosition
        ? createPortal(
            <div
              className="opening-balance-mapping-popover"
              style={mappingPosition}
              role="dialog"
              aria-label={`Change recipient mapping for ${mappingRow.adviser}`}
              onClick={(event) => event.stopPropagation()}
            >
              <IconButton
                className="opening-balance-mapping-close"
                icon={<CloseIcon aria-hidden="true" />}
                label="Close recipient mapping editor"
                disabled={mappingSaving}
                onClick={closeMapping}
              />
              <CustomSelect
                label={`Map ${mappingRow.adviser} to`}
                options={recipientMappingOptions}
                value={mappingRow.email
                  ? `adviser:${mappingRow.email}`
                  : mappingRow.mappedEntityType === "introducer" && mappingRow.mappedEntityId
                    ? `introducer:${mappingRow.mappedEntityId}`
                    : ""}
                placeholder="Select an adviser or IO introducer"
                searchPlaceholder="Search adviser, email, or IO introducer"
                required
                clearable={false}
                disabled={mappingSaving || loadingAdvisersWithUnappointed || ioIntroducersLoading}
                overlay={mappingSaving}
                onChange={(event) => saveMapping(event.target.value)}
              />
              {mappingError ? (
                <p className="FP_Content text-error" role="alert">
                  {mappingError}
                </p>
              ) : null}
              <div className="opening-balance-mapping-actions">
                <SecondaryButton
                  label="Cancel"
                  disabled={mappingSaving}
                  onClick={closeMapping}
                />
              </div>
            </div>,
            document.body,
          )
        : null}
    </section>
  );
};

export default OpeningBalances;
